import { useMemo } from 'react';
import { Skeleton, Stack } from '@mui/material';

import { RetailerNetworkLocationGroupFragment } from '@occc/graphql';

import { LocationGroupSection, OTHERS_GROUP_ID } from './location-group-section.component';

import { ConnectionStatus } from '../retailer-network-my-stores-v2-page.types';

const OTHERS_GROUP = { uuid: OTHERS_GROUP_ID, name: 'Others' } as const;
const SKELETON_ROWS = 4;

type StoresGroupedListProps = {
  locationGroups: RetailerNetworkLocationGroupFragment[];
  isLoading: boolean;
  networkId: string;
  stateFilter?: string;
  cityFilter?: string;
  connectionStatusFilter?: ConnectionStatus | '';
};

export const StoresGroupedList = ({
  locationGroups,
  isLoading,
  networkId,
  stateFilter,
  cityFilter,
  connectionStatusFilter,
}: StoresGroupedListProps) => {
  const allGroupUuids = useMemo(() => locationGroups.map((g) => g.uuid), [locationGroups]);
  const nonEmptyGroups = useMemo(
    () => locationGroups.filter((g) => g.countOfLocations > 0),
    [locationGroups],
  );

  // Force-remount each section on filter change so its own pagination cursor resets to page 1
  // instead of potentially continuing from a stale cursor under the new filter.
  const filtersKey = `${stateFilter ?? ''}|${cityFilter ?? ''}|${connectionStatusFilter ?? ''}`;

  if (isLoading && locationGroups.length === 0) {
    return (
      <Stack spacing={1}>
        {Array.from({ length: SKELETON_ROWS }, (_, i) => (
          <Skeleton key={i} variant='rounded' height={56} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={1}>
      {nonEmptyGroups.map((group) => (
        <LocationGroupSection
          key={`${group.uuid}-${filtersKey}`}
          group={group}
          networkId={networkId}
          allGroupUuids={allGroupUuids}
          stateFilter={stateFilter}
          cityFilter={cityFilter}
          connectionStatusFilter={connectionStatusFilter}
        />
      ))}

      <LocationGroupSection
        key={`${OTHERS_GROUP_ID}-${filtersKey}`}
        group={OTHERS_GROUP}
        networkId={networkId}
        allGroupUuids={allGroupUuids}
        stateFilter={stateFilter}
        cityFilter={cityFilter}
        connectionStatusFilter={connectionStatusFilter}
        hideWhenEmpty
      />
    </Stack>
  );
};
